import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import { useUpdateVenuaPlannerProduct } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import {
  buildProductDetailFormSchema,
  emptyProductDetailFormValues,
  type ProductDetailFormValues,
} from './productDetailFormSchema';
import { toProductDetailRequestBody } from './toProductDetailRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
  DestinationIds: 'destinations',
};

interface UseProductDetailFormOptions {
  /** Produkt, který se edituje. */
  productId: string;
  /** Výchozí hodnoty namapované z detailu produktu. */
  initialValues?: ProductDetailFormValues;
  /** Voláno po úspěšném uložení (předává id produktu) — invalidace, toast; NEzavírá okno. */
  onSaved: (id: string) => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na pole. */
  onError?: (error: unknown) => void;
}

/**
 * Formulářová logika detailu produktu — TanStack Form se Zod validací, update mutace a promítnutí
 * serverových validačních chyb do polí. `submit()` vrací, zda se uložení povedlo (pro „Uložit & zpět").
 */
export function useProductDetailForm({
  productId,
  initialValues,
  onSaved,
  onError,
}: UseProductDetailFormOptions) {
  const { t } = useTranslation('venua-planner');
  const schema = useMemo(() => buildProductDetailFormSchema(t), [t]);

  const updateMutation = useUpdateVenuaPlannerProduct();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues ?? emptyProductDetailFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      try {
        await updateMutation.mutateAsync({
          productId,
          data: toProductDetailRequestBody(value),
        });
        onSaved(productId);
        successRef.current = true;
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  /** Spustí validaci + uložení; vrací `true` při úspěchu (pro „Uložit & zpět"). */
  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return { form, submit, isSubmitting: updateMutation.isPending };
}
